import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
    Settings as SettingsIcon, 
    Brain, 
    Mic, 
    MessageSquare, 
    Save,
    Target,
    TrendingUp,
    Users,
    Briefcase,
    MessageCircle,
    DollarSign,
    FileText,
    Zap,
    GraduationCap,
    BookOpen,
    Volume2,
    Loader2,
    RefreshCw,
    AlertTriangle,
    Video,
    CloudOff,
    Cloud,
    Calendar,
    Award,
    BarChart3,
    Keyboard
} from 'lucide-react';

import PlaybookManager from '../components/admin/PlaybookManager';
import EvolveSettings from '../components/settings/EvolveSettings';
import SeedManagement from '../components/settings/SeedManagement';
import TypingTestSettings from '../components/settings/TypingTestSettings';
import CertificationThresholdSettings from '../components/settings/CertificationThresholdSettings';
import EEOCScoringSettings from '../components/settings/EEOCScoringSettings';
import Phase7RolePlaySettings from '../components/settings/Phase7RolePlaySettings';
import MaintenanceModeSettings from '../components/settings/MaintenanceModeSettings';
import { FocusGuardSettings } from '../components/settings/FocusGuardSettings';

// Refactored Admin Components
import { 
    SystemHealthCard, 
    DatabaseMaintenanceCard, 
    EvolveAnalysisCard 
} from '../components/settings/admin';
import UserRetryResetPanel from '../components/admin/UserRetryResetPanel';
import RPATAttemptResetPanel from '../components/admin/RPATAttemptResetPanel';
import CourseRetryResetPanel from '../components/admin/CourseRetryResetPanel';
import ProgressResetPanel from '../components/admin/progress-reset/ProgressResetPanel';
import CourseModulesManager from '../components/admin/CourseModulesManager';
import QuizReviewTool from '../components/admin/QuizReviewTool';
import NaturalnessReviewPanel from '../components/admin/NaturalnessReviewPanel';
import SourcingSessionManager from '../components/admin/SourcingSessionManager';
import ScreenshotViolationsTable from '../components/settings/admin/ScreenshotViolationsTable';
import CandidatePoolManager from '../components/admin/CandidatePoolManager';
import RPATProgressRepairCard from '../components/settings/RPATProgressRepairCard';

const difficultyOptions = [
    { value: 'easy', label: 'Easy', description: 'Beginner-friendly, supportive', color: 'bg-emerald-500' },
    { value: 'medium', label: 'Medium', description: 'Realistic workplace challenges', color: 'bg-amber-500' },
    { value: 'tough', label: 'Tough', description: 'Expert-level scenarios', color: 'bg-red-500' },
    { value: 'progressive', label: 'Progressive', description: 'Auto-increases with practice', color: 'bg-violet-500' }
];

const simulationTypes = [
    { id: 'candidate_screening', label: 'Candidate Screening', icon: Users },
    { id: 'client_intake', label: 'Client Intake', icon: Briefcase },
    { id: 'objection_handling', label: 'Objection Handling', icon: MessageCircle },
    { id: 'salary_negotiation', label: 'Salary Negotiation', icon: DollarSign },
    { id: 'job_analysis', label: 'Job Analysis', icon: FileText },
    { id: 'communication', label: 'Communication', icon: MessageSquare }
];

const Settings = () => {
    const { api, user } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // User preferences state (AI voice/name)
    const [userPrefs, setUserPrefs] = useState(null);
    const [availableVoices, setAvailableVoices] = useState(null);
    const [prefsLoading, setPrefsLoading] = useState(false);
    const [prefsSaving, setPrefsSaving] = useState(false);

    useEffect(() => {
        fetchSettings();
        fetchUserPreferences();
    }, []);
    
    const fetchUserPreferences = async () => {
        setPrefsLoading(true);
        try {
            const response = await api.get('/user/preferences');
            setUserPrefs(response.data.preferences);
            setAvailableVoices(response.data.available_voices);
        } catch (error) {
            console.error('Failed to load user preferences:', error);
        } finally {
            setPrefsLoading(false);
        }
    };
    
    const saveUserPreferences = async (updates) => {
        setPrefsSaving(true);
        try {
            const params = new URLSearchParams();
            Object.entries(updates).forEach(([key, value]) => {
                // Include empty string values (allows clearing AI name)
                if (value !== undefined && value !== null) {
                    params.append(key, String(value));
                }
            });
            
            const response = await api.put(`/user/preferences?${params.toString()}`);
            setUserPrefs(response.data.preferences);
            
            // Emit event so RPATLesson can refresh audio
            window.dispatchEvent(new CustomEvent('voicePreferencesChanged', { 
                detail: response.data.preferences 
            }));
            
            toast.success('AI preferences saved! Changes will apply to new audio.');
        } catch (error) {
            toast.error('Failed to save preferences');
        } finally {
            setPrefsSaving(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await api.get('/settings');
            // Initialize simulation_difficulty if not present
            if (!response.data.simulation_difficulty) {
                response.data.simulation_difficulty = {
                    global_difficulty: 'easy',
                    per_simulation_difficulty: {
                        candidate_screening: 'easy',
                        client_intake: 'easy',
                        objection_handling: 'easy',
                        salary_negotiation: 'easy',
                        job_analysis: 'easy',
                        communication: 'easy'
                    },
                    progressive_medium_threshold: 5,
                    progressive_tough_threshold: 10
                };
            }
            // Initialize job_simulation_settings if not present
            if (!response.data.job_simulation_settings) {
                response.data.job_simulation_settings = {
                    enabled: true,
                    allow_skip_lessons: false,
                    require_quiz_pass: true,
                    quiz_pass_threshold: 70,
                    max_quiz_attempts: 3,
                    progressive_difficulty_enabled: true,
                    simulations_for_difficulty_increase: 10,
                    voice_enabled: true,
                    auto_generate_playbooks: true
                };
            }
            setSettings(response.data);
        } catch (error) {
            console.error('Failed to load settings:', error);
            toast.error('Failed to load settings');
            // Set default settings so page doesn't crash
            setSettings({
                id: 'default',
                primary_llm: 'gpt-4o',
                backup_llm: 'gpt-4o-mini',
                passing_score: 70,
                voice_scoring_weights: {
                    pace: 25,
                    clarity: 25,
                    fillers: 25,
                    confidence: 25
                },
                communication_scoring_weights: {
                    structure: 25,
                    engagement: 25,
                    relevance: 25,
                    professionalism: 25
                },
                simulation_difficulty: {
                    global_difficulty: 'easy',
                    per_simulation_difficulty: {}
                },
                job_simulation_settings: {
                    enabled: true,
                    allow_skip_lessons: false,
                    require_quiz_pass: true,
                    quiz_pass_threshold: 70,
                    max_quiz_attempts: 3,
                    progressive_difficulty_enabled: true,
                    simulations_for_difficulty_increase: 10,
                    voice_enabled: true,
                    auto_generate_playbooks: true
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            await api.put('/settings', settings);
            toast.success('Settings saved successfully');
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const updateVoiceWeight = (key, value) => {
        setSettings(prev => ({
            ...prev,
            voice_scoring_weights: {
                ...prev.voice_scoring_weights,
                [key]: value / 100
            }
        }));
    };

    const updateCommWeight = (key, value) => {
        setSettings(prev => ({
            ...prev,
            communication_scoring_weights: {
                ...prev.communication_scoring_weights,
                [key]: value / 100
            }
        }));
    };

    const updateGlobalDifficulty = (value) => {
        setSettings(prev => ({
            ...prev,
            simulation_difficulty: {
                ...prev.simulation_difficulty,
                global_difficulty: value,
                // When changing global, update all per-simulation settings to match
                per_simulation_difficulty: Object.fromEntries(
                    simulationTypes.map(st => [st.id, value])
                )
            }
        }));
    };

    const updatePerSimulationDifficulty = (simType, value) => {
        setSettings(prev => ({
            ...prev,
            simulation_difficulty: {
                ...prev.simulation_difficulty,
                per_simulation_difficulty: {
                    ...prev.simulation_difficulty?.per_simulation_difficulty,
                    [simType]: value
                }
            }
        }));
    };

    const updateProgressiveThreshold = (key, value) => {
        setSettings(prev => ({
            ...prev,
            simulation_difficulty: {
                ...prev.simulation_difficulty,
                [key]: value
            }
        }));
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </MainLayout>
        );
    }

    if (!settings) {
        return (
            <MainLayout>
                <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
                    <AlertTriangle className="h-12 w-12 text-amber-500" />
                    <h2 className="text-xl font-semibold">Unable to load settings</h2>
                    <p className="text-muted-foreground">Please try refreshing the page</p>
                    <Button onClick={() => window.location.reload()}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh Page
                    </Button>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="settings-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <SettingsIcon className="h-8 w-8 text-primary" />
                            Settings
                        </h1>
                        <p className="text-muted-foreground mt-1">Configure platform settings</p>
                    </div>
                    <Button onClick={saveSettings} disabled={saving} className="btn-primary" data-testid="save-settings">
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>

                {/* System Health & Skills Card - Refactored Component */}
                <SystemHealthCard api={api} />

                {/* Maintenance Mode Settings - Admin Only - CRITICAL CONTROL */}
                {user?.role === 'admin' && (
                    <MaintenanceModeSettings />
                )}

                {/* Focus Guard - learner anti-malpractice switches */}
                {user?.role === 'admin' && <FocusGuardSettings />}

                {/* R-PAT Progress Repair - Admin Only */}
                {user?.role === 'admin' && (
                    <RPATProgressRepairCard api={api} />
                )}

                {/* Database Maintenance Card - Admin Only - Refactored Component */}
                {user?.role === 'admin' && (
                    <DatabaseMaintenanceCard api={api} />
                )}

                {/* Evolve AI Analysis Card - Admin Only - Refactored Component */}
                {user?.role === 'admin' && (
                    <EvolveAnalysisCard api={api} />
                )}

                {/* User Retry Reset Panel - Admin Only */}
                {user?.role === 'admin' && (
                    <UserRetryResetPanel api={api} />
                )}

                {/* R-PAT Assessment Attempt Reset - Admin Only */}
                {user?.role === 'admin' && (
                    <RPATAttemptResetPanel api={api} />
                )}

                {/* Course Retry Reset Panel - Admin Only (Tech Learning & Finance Training) */}
                {user?.role === 'admin' && (
                    <CourseRetryResetPanel api={api} />
                )}

                {/* Progress Reset by Department - Admin Only (all course builders) */}
                {user?.role === 'admin' && (
                    <ProgressResetPanel api={api} />
                )}

                {/* Course Modules Manager - Admin Only */}
                {user?.role === 'admin' && (
                    <CourseModulesManager api={api} />
                )}

                {/* Quiz Review Tool - Admin Only */}
                {user?.role === 'admin' && (
                    <QuizReviewTool api={api} />
                )}

                {/* Naturalness Review Panel - Admin Only */}
                {user?.role === 'admin' && (
                    <NaturalnessReviewPanel />
                )}

                {/* Sourcing Session Manager - Admin Only */}
                {user?.role === 'admin' && (
                    <SourcingSessionManager />
                )}

                {/* Candidate Pool Manager - Admin Only */}
                {user?.role === 'admin' && (
                    <CandidatePoolManager />
                )}

                {/* Screenshot Violations Table - Admin Only */}
                {user?.role === 'admin' && (
                    <ScreenshotViolationsTable />
                )}

                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Simulation Difficulty Settings - NEW PROMINENT SECTION */}
                    <Card className="lg:col-span-2 border-2 border-primary/20">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-secondary/5">
                            <CardTitle className="flex items-center gap-2">
                                <Target className="h-5 w-5 text-primary" />
                                Simulation Difficulty Settings
                            </CardTitle>
                            <CardDescription>
                                Control the difficulty level for all simulations. Admin-configured settings apply to all users.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6 pt-6">
                            {/* Global Difficulty */}
                            <div className="space-y-3">
                                <Label className="text-base font-semibold flex items-center gap-2">
                                    <Zap className="h-4 w-4 text-amber-500" />
                                    Global Difficulty Level
                                </Label>
                                <p className="text-sm text-muted-foreground">
                                    This sets the default difficulty for all simulation types
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {difficultyOptions.map((option) => {
                                        const isSelected = settings?.simulation_difficulty?.global_difficulty === option.value;
                                        return (
                                            <button
                                                key={option.value}
                                                onClick={() => updateGlobalDifficulty(option.value)}
                                                className={`p-4 rounded-xl border-2 transition-all text-left ${
                                                    isSelected 
                                                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                                                        : 'border-muted hover:border-primary/50'
                                                }`}
                                                data-testid={`global-difficulty-${option.value}`}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <div className={`w-3 h-3 rounded-full ${option.color}`} />
                                                    <span className="font-semibold">{option.label}</span>
                                                </div>
                                                <p className="text-xs text-muted-foreground">{option.description}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Progressive Mode Thresholds */}
                            {settings?.simulation_difficulty?.global_difficulty === 'progressive' && (
                                <div className="p-4 rounded-xl bg-violet-500/5 border border-violet-500/20 space-y-4">
                                    <Label className="text-sm font-semibold flex items-center gap-2 text-violet-700 dark:text-violet-400">
                                        <TrendingUp className="h-4 w-4" />
                                        Progressive Mode Thresholds
                                    </Label>
                                    <p className="text-xs text-muted-foreground">
                                        Users start at Easy and automatically progress based on completed simulations per type
                                    </p>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm">Medium after (simulations)</Label>
                                            <div className="flex items-center gap-3">
                                                <Slider
                                                    value={[settings?.simulation_difficulty?.progressive_medium_threshold || 5]}
                                                    onValueChange={([value]) => updateProgressiveThreshold('progressive_medium_threshold', value)}
                                                    min={2}
                                                    max={15}
                                                    step={1}
                                                    className="flex-1"
                                                />
                                                <Badge variant="outline" className="w-10 justify-center">
                                                    {settings?.simulation_difficulty?.progressive_medium_threshold || 5}
                                                </Badge>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm">Tough after (simulations)</Label>
                                            <div className="flex items-center gap-3">
                                                <Slider
                                                    value={[settings?.simulation_difficulty?.progressive_tough_threshold || 10]}
                                                    onValueChange={([value]) => updateProgressiveThreshold('progressive_tough_threshold', value)}
                                                    min={5}
                                                    max={30}
                                                    step={1}
                                                    className="flex-1"
                                                />
                                                <Badge variant="outline" className="w-10 justify-center">
                                                    {settings?.simulation_difficulty?.progressive_tough_threshold || 10}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <Separator />

                            {/* Per-Simulation Type Settings */}
                            <div className="space-y-3">
                                <Label className="text-base font-semibold">Per-Simulation Type Control</Label>
                                <p className="text-sm text-muted-foreground">
                                    Override global difficulty for specific simulation types
                                </p>
                                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                                    {simulationTypes.map((simType) => {
                                        const Icon = simType.icon;
                                        const currentDifficulty = settings?.simulation_difficulty?.per_simulation_difficulty?.[simType.id] || 'easy';
                                        const difficultyColor = difficultyOptions.find(d => d.value === currentDifficulty)?.color || 'bg-emerald-500';
                                        
                                        return (
                                            <div 
                                                key={simType.id} 
                                                className="p-3 rounded-lg border bg-muted/30 space-y-2"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Icon className="h-4 w-4 text-muted-foreground" />
                                                    <span className="text-sm font-medium">{simType.label}</span>
                                                    <div className={`w-2 h-2 rounded-full ml-auto ${difficultyColor}`} />
                                                </div>
                                                <Select
                                                    value={currentDifficulty}
                                                    onValueChange={(value) => updatePerSimulationDifficulty(simType.id, value)}
                                                >
                                                    <SelectTrigger className="h-8 text-xs" data-testid={`difficulty-${simType.id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {difficultyOptions.map((option) => (
                                                            <SelectItem key={option.value} value={option.value}>
                                                                <div className="flex items-center gap-2">
                                                                    <div className={`w-2 h-2 rounded-full ${option.color}`} />
                                                                    {option.label}
                                                                </div>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Job Simulation Settings - NEW SECTION */}
                    <JobSimulationSettingsCard 
                        settings={settings} 
                        setSettings={setSettings}
                        api={api}
                    />

                    {/* Sourcing Labs Settings */}
                    <SourcingLabsSettingsCard 
                        settings={settings} 
                        setSettings={setSettings}
                        api={api}
                    />

                    {/* EVOLVE Presentation Coaching Settings */}
                    <EvolveSettings />

                    {/* R-PAT Typing Test Settings */}
                    <TypingTestSettings />

                    {/* R-PAT Phase 7 Role Play Settings */}
                    <Phase7RolePlaySettings />

                    {/* R-PAT Certification Threshold Settings */}
                    <CertificationThresholdSettings />

                    {/* EEOC Compliance Scoring Settings */}
                    <EEOCScoringSettings />
                    
                    {/* AI Voice & Personality Settings - Available for all users */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Volume2 className="h-5 w-5 text-primary" />
                                AI Assistant Customization
                            </CardTitle>
                            <CardDescription>Personalize your AI trainer&apos;s voice and personality</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {prefsLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : (
                                <>
                                    {/* AI Name */}
                                    <div className="space-y-2">
                                        <Label>AI Assistant Name</Label>
                                        <Input
                                            value={userPrefs?.ai_name || 'Alex'}
                                            onChange={(e) => setUserPrefs({ ...userPrefs, ai_name: e.target.value })}
                                            placeholder="Enter a name for your AI assistant"
                                            maxLength={20}
                                            data-testid="ai-name-input"
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Give your AI trainer a personalized name (max 20 characters)
                                        </p>
                                    </div>
                                    
                                    {/* Voice Language */}
                                    <div className="space-y-2">
                                        <Label>Voice Accent</Label>
                                        <Select
                                            value={userPrefs?.voice_language || 'en-us'}
                                            onValueChange={(value) => {
                                                setUserPrefs({ ...userPrefs, voice_language: value, voice_id: null });
                                            }}
                                        >
                                            <SelectTrigger data-testid="voice-language-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="en-us">🇺🇸 American English</SelectItem>
                                                <SelectItem value="en-gb">🇬🇧 British English</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    {/* Voice Selection */}
                                    {availableVoices && (
                                        <div className="space-y-4">
                                            <Label>Voice Selection</Label>
                                            
                                            {/* Female Voices */}
                                            <div className="space-y-2">
                                                <span className="text-sm font-medium text-muted-foreground">Female Voices</span>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                    {availableVoices[userPrefs?.voice_language || 'en-us']?.female?.map((voice) => (
                                                        <button
                                                            key={voice.id}
                                                            onClick={() => setUserPrefs({ ...userPrefs, voice_id: voice.id })}
                                                            className={`p-3 rounded-lg border text-left transition-all ${
                                                                userPrefs?.voice_id === voice.id 
                                                                    ? 'border-primary bg-primary/10' 
                                                                    : 'border-muted hover:border-primary/50'
                                                            }`}
                                                            data-testid={`voice-${voice.id}`}
                                                        >
                                                            <div className="font-medium text-sm">{voice.name}</div>
                                                            <div className="text-xs text-muted-foreground">{voice.description}</div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {/* Male Voices */}
                                            <div className="space-y-2">
                                                <span className="text-sm font-medium text-muted-foreground">Male Voices</span>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                    {availableVoices[userPrefs?.voice_language || 'en-us']?.male?.map((voice) => (
                                                        <button
                                                            key={voice.id}
                                                            onClick={() => setUserPrefs({ ...userPrefs, voice_id: voice.id })}
                                                            className={`p-3 rounded-lg border text-left transition-all ${
                                                                userPrefs?.voice_id === voice.id 
                                                                    ? 'border-primary bg-primary/10' 
                                                                    : 'border-muted hover:border-primary/50'
                                                            }`}
                                                            data-testid={`voice-${voice.id}`}
                                                        >
                                                            <div className="font-medium text-sm">{voice.name}</div>
                                                            <div className="text-xs text-muted-foreground">{voice.description}</div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Save Button */}
                                    <Button 
                                        onClick={() => saveUserPreferences({
                                            ai_name: userPrefs?.ai_name,
                                            voice_id: userPrefs?.voice_id,
                                            voice_language: userPrefs?.voice_language
                                        })}
                                        disabled={prefsSaving}
                                        className="w-full"
                                        data-testid="save-ai-prefs"
                                    >
                                        {prefsSaving ? (
                                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                                        ) : (
                                            <><Save className="h-4 w-4 mr-2" /> Save AI Preferences</>
                                        )}
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    {/* Seed Management - Admin only */}
                    <SeedManagement />

                    {/* LLM Configuration */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Brain className="h-5 w-5 text-primary" />
                                LLM Configuration
                            </CardTitle>
                            <CardDescription>Configure AI model settings</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Primary LLM</Label>
                                <Select
                                    value={settings?.primary_llm || 'lemonfox'}
                                    onValueChange={(value) => setSettings({ ...settings, primary_llm: value })}
                                >
                                    <SelectTrigger data-testid="primary-llm-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="lemonfox">Lemonfox (Llama 70B)</SelectItem>
                                        <SelectItem value="emergent">Emergent (GPT-5.2)</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    Primary AI model for training and simulations
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label>Backup LLM</Label>
                                <Select
                                    value={settings?.backup_llm || 'emergent'}
                                    onValueChange={(value) => setSettings({ ...settings, backup_llm: value })}
                                >
                                    <SelectTrigger data-testid="backup-llm-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="emergent">Emergent (GPT-5.2)</SelectItem>
                                        <SelectItem value="lemonfox">Lemonfox (Llama 70B)</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    Fallback model if primary fails
                                </p>
                            </div>

                            <Separator />

                            <div className="space-y-2">
                                <Label>Passing Score</Label>
                                <div className="flex items-center gap-4">
                                    <Slider
                                        value={[settings?.passing_score || 70]}
                                        onValueChange={([value]) => setSettings({ ...settings, passing_score: value })}
                                        min={50}
                                        max={100}
                                        step={5}
                                        className="flex-1"
                                    />
                                    <span className="w-12 text-right font-bold">{settings?.passing_score || 70}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Minimum score required to pass assessments
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Voice Scoring Weights */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Mic className="h-5 w-5 text-secondary" />
                                Voice Scoring Weights
                            </CardTitle>
                            <CardDescription>Adjust voice evaluation criteria importance</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {settings?.voice_scoring_weights && Object.entries(settings.voice_scoring_weights).map(([key, value]) => (
                                <div key={key} className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label className="capitalize">{key.replace('_', ' ')}</Label>
                                        <span className="text-sm font-medium">{Math.round(value * 100)}%</span>
                                    </div>
                                    <Slider
                                        value={[value * 100]}
                                        onValueChange={([v]) => updateVoiceWeight(key, v)}
                                        min={0}
                                        max={50}
                                        step={5}
                                    />
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    {/* Communication Scoring Weights */}
                    <Card className="lg:col-span-2">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <MessageSquare className="h-5 w-5 text-emerald-500" />
                                Communication Scoring Weights
                            </CardTitle>
                            <CardDescription>Adjust communication evaluation criteria importance</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-5">
                                {settings?.communication_scoring_weights && Object.entries(settings.communication_scoring_weights).map(([key, value]) => (
                                    <div key={key} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="capitalize text-sm">{key}</Label>
                                            <span className="text-sm font-medium">{Math.round(value * 100)}%</span>
                                        </div>
                                        <Slider
                                            value={[value * 100]}
                                            onValueChange={([v]) => updateCommWeight(key, v)}
                                            min={0}
                                            max={50}
                                            step={5}
                                        />
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </MainLayout>
    );
};

// Job Simulation Settings Component
const JobSimulationSettingsCard = ({ settings, setSettings, api }) => {
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [showPlaybookManager, setShowPlaybookManager] = useState(false);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoadingStats(true);
        try {
            const response = await api.get('/job-simulations/admin/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch job simulation stats:', error);
        } finally {
            setLoadingStats(false);
        }
    };

    const jobSimSettings = settings?.job_simulation_settings || {
        enabled: true,
        allow_skip_lessons: false,
        require_quiz_pass: true,
        quiz_pass_threshold: 70,
        max_quiz_attempts: 3,
        progressive_difficulty_enabled: true,
        simulations_for_difficulty_increase: 10,
        voice_enabled: true,
        auto_generate_playbooks: true
    };

    const updateJobSimSetting = (key, value) => {
        setSettings(prev => ({
            ...prev,
            job_simulation_settings: {
                ...prev.job_simulation_settings,
                [key]: value
            }
        }));
    };

    return (
        <Card className="lg:col-span-2 border-2 border-blue-500/20">
            <CardHeader className="bg-gradient-to-r from-blue-500/5 to-cyan-500/5">
                <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-blue-500" />
                    Job Simulation Settings
                </CardTitle>
                <CardDescription>
                    Configure skill training and job simulation features for recruiters
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
                {/* Stats Overview */}
                {stats && (
                    <div className="grid grid-cols-5 gap-4 p-4 rounded-xl bg-muted/30">
                        <div className="text-center">
                            <p className="text-2xl font-bold text-blue-500">{stats.total_skills}</p>
                            <p className="text-xs text-muted-foreground">Total Skills</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-emerald-500">{stats.total_playbooks}</p>
                            <p className="text-xs text-muted-foreground">Playbooks</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-amber-500">{stats.users_learning}</p>
                            <p className="text-xs text-muted-foreground">Users Learning</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-violet-500">{stats.skills_completed}</p>
                            <p className="text-xs text-muted-foreground">Completed</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold">{stats.total_simulations}</p>
                            <p className="text-xs text-muted-foreground">Simulations</p>
                        </div>
                    </div>
                )}

                {/* Playbook Management */}
                <div className="space-y-4 p-4 rounded-xl border bg-muted/20">
                    <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setShowPlaybookManager(!showPlaybookManager)}
                    >
                        <div>
                            <Label className="text-base font-semibold flex items-center gap-2 cursor-pointer">
                                <BookOpen className="h-4 w-4 text-blue-500" />
                                AI Playbook Management
                            </Label>
                            <p className="text-sm text-muted-foreground mt-1">
                                View status and retry failed playbook generations
                            </p>
                        </div>
                        <Button 
                            variant="outline"
                            className="gap-2"
                            data-testid="toggle-playbook-manager"
                        >
                            {showPlaybookManager ? 'Hide' : 'Manage Playbooks'}
                        </Button>
                    </div>
                    
                    {showPlaybookManager && (
                        <div className="pt-4 border-t mt-4">
                            <PlaybookManager />
                        </div>
                    )}
                </div>

                <Separator />

                {/* Settings Grid */}
                <div className="grid gap-6 md:grid-cols-2">
                    {/* Feature Toggles */}
                    <div className="space-y-4">
                        <Label className="text-base font-semibold">Feature Controls</Label>
                        
                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <Label className="text-sm font-medium">Enable Job Simulations</Label>
                                <p className="text-xs text-muted-foreground">Allow users to access skill training</p>
                            </div>
                            <Switch
                                checked={jobSimSettings.enabled}
                                onCheckedChange={(checked) => updateJobSimSetting('enabled', checked)}
                                data-testid="job-sim-enabled"
                            />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <Label className="text-sm font-medium">Allow Skip Lessons</Label>
                                <p className="text-xs text-muted-foreground">Let users skip to simulations</p>
                            </div>
                            <Switch
                                checked={jobSimSettings.allow_skip_lessons}
                                onCheckedChange={(checked) => updateJobSimSetting('allow_skip_lessons', checked)}
                                data-testid="job-sim-skip-lessons"
                            />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <Label className="text-sm font-medium">Require Quiz Pass</Label>
                                <p className="text-xs text-muted-foreground">Must pass quiz to proceed</p>
                            </div>
                            <Switch
                                checked={jobSimSettings.require_quiz_pass}
                                onCheckedChange={(checked) => updateJobSimSetting('require_quiz_pass', checked)}
                                data-testid="job-sim-require-quiz"
                            />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <Label className="text-sm font-medium flex items-center gap-2">
                                    <Volume2 className="h-4 w-4" />
                                    Voice Narration
                                </Label>
                                <p className="text-xs text-muted-foreground">Enable TTS for lessons</p>
                            </div>
                            <Switch
                                checked={jobSimSettings.voice_enabled}
                                onCheckedChange={(checked) => updateJobSimSetting('voice_enabled', checked)}
                                data-testid="job-sim-voice"
                            />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <Label className="text-sm font-medium flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4" />
                                    Progressive Difficulty
                                </Label>
                                <p className="text-xs text-muted-foreground">Auto-increase difficulty</p>
                            </div>
                            <Switch
                                checked={jobSimSettings.progressive_difficulty_enabled}
                                onCheckedChange={(checked) => updateJobSimSetting('progressive_difficulty_enabled', checked)}
                                data-testid="job-sim-progressive"
                            />
                        </div>
                    </div>

                    {/* Numeric Settings */}
                    <div className="space-y-4">
                        <Label className="text-base font-semibold">Thresholds & Limits</Label>

                        <div className="p-3 rounded-lg border space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Quiz Pass Threshold</Label>
                                <Badge variant="outline">{jobSimSettings.quiz_pass_threshold}%</Badge>
                            </div>
                            <Slider
                                value={[jobSimSettings.quiz_pass_threshold]}
                                onValueChange={([value]) => updateJobSimSetting('quiz_pass_threshold', value)}
                                min={50}
                                max={100}
                                step={5}
                            />
                            <p className="text-xs text-muted-foreground">Minimum score to pass lesson quizzes</p>
                        </div>

                        <div className="p-3 rounded-lg border space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Max Quiz Attempts</Label>
                                <Badge variant="outline">{jobSimSettings.max_quiz_attempts}</Badge>
                            </div>
                            <Slider
                                value={[jobSimSettings.max_quiz_attempts]}
                                onValueChange={([value]) => updateJobSimSetting('max_quiz_attempts', value)}
                                min={1}
                                max={10}
                                step={1}
                            />
                            <p className="text-xs text-muted-foreground">Attempts before questions regenerate</p>
                        </div>

                        <div className="p-3 rounded-lg border space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Difficulty Increase After</Label>
                                <Badge variant="outline">{jobSimSettings.simulations_for_difficulty_increase} sims</Badge>
                            </div>
                            <Slider
                                value={[jobSimSettings.simulations_for_difficulty_increase]}
                                onValueChange={([value]) => updateJobSimSetting('simulations_for_difficulty_increase', value)}
                                min={5}
                                max={25}
                                step={5}
                            />
                            <p className="text-xs text-muted-foreground">Simulations before difficulty increases</p>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// Sourcing Labs Settings Component
const SourcingLabsSettingsCard = ({ settings, setSettings, api }) => {
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoadingStats(true);
        try {
            const response = await api.get('/sourcing-labs/admin/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch sourcing labs stats:', error);
        } finally {
            setLoadingStats(false);
        }
    };

    const seedLabs = async () => {
        setSeeding(true);
        try {
            const response = await api.post('/sourcing-labs/admin/seed-labs');
            toast.success(response.data.message);
            fetchStats();
        } catch (error) {
            toast.error('Failed to seed labs');
        } finally {
            setSeeding(false);
        }
    };

    const saveSourcingSettings = async () => {
        setSavingSettings(true);
        try {
            await api.put('/settings', { sourcing_lab_settings: sourcingSettings });
            toast.success('Sourcing Labs settings saved successfully!');
        } catch (error) {
            console.error('Failed to save sourcing settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const SOURCING_DEFAULTS = {
        enabled: true,
        dynamic_difficulty_enabled: true,
        sessions_for_difficulty_increase: 5,
        score_threshold_for_increase: 60,
        candidates_per_session: 10,
        min_evaluations_required: 5,
        ai_hints_enabled: true,
        max_hints_per_session: 3,
        search_string_weight: 0.2,
        candidate_selection_weight: 0.4,
        reasoning_quality_weight: 0.4,
        time_limit_minutes: 0,
        show_correct_answers_after: true,
        allow_retry: true
    };

    const sourcingSettings = {
        ...SOURCING_DEFAULTS,
        ...(settings?.sourcing_lab_settings || {})
    };

    const updateSourcingSetting = (key, value) => {
        setSettings(prev => ({
            ...prev,
            sourcing_lab_settings: {
                ...prev.sourcing_lab_settings,
                [key]: value
            }
        }));
    };

    return (
        <Card className="lg:col-span-2 border-2 border-emerald-500/20">
            <CardHeader className="bg-gradient-to-r from-emerald-500/5 to-teal-500/5">
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Target className="h-5 w-5 text-emerald-500" />
                            Sourcing Labs Settings
                        </CardTitle>
                        <CardDescription>
                            Configure candidate sourcing training features for recruiters
                        </CardDescription>
                    </div>
                    <Button 
                        onClick={saveSourcingSettings} 
                        disabled={savingSettings}
                        className="bg-emerald-500 hover:bg-emerald-600"
                        data-testid="save-sourcing-settings-btn"
                    >
                        {savingSettings ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                Save Settings
                            </>
                        )}
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
                {/* Stats Overview */}
                {stats && (
                    <div className="grid grid-cols-4 gap-4 p-4 rounded-xl bg-muted/30">
                        <div className="text-center">
                            <p className="text-2xl font-bold text-emerald-500">{stats.total_labs}</p>
                            <p className="text-xs text-muted-foreground">Labs</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-blue-500">{stats.total_sessions}</p>
                            <p className="text-xs text-muted-foreground">Sessions</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-amber-500">{stats.completed_sessions}</p>
                            <p className="text-xs text-muted-foreground">Completed</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-violet-500">{stats.completion_rate}%</p>
                            <p className="text-xs text-muted-foreground">Completion Rate</p>
                        </div>
                    </div>
                )}

                {/* Seed Labs Button */}
                <div className="flex items-center justify-between p-4 rounded-xl border bg-muted/20">
                    <div>
                        <Label className="text-base font-semibold">Initialize Default Labs</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                            Create labs for all available industries
                        </p>
                    </div>
                    <Button 
                        onClick={seedLabs}
                        disabled={seeding}
                        variant="outline"
                        className="gap-2"
                        data-testid="seed-sourcing-labs"
                    >
                        {seeding ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4" />
                        )}
                        {seeding ? 'Seeding...' : 'Seed Labs'}
                    </Button>
                </div>

                <Separator />

                {/* Settings Grid */}
                <div className="grid gap-6 md:grid-cols-2">
                    {/* Feature Toggles */}
                    <div className="space-y-4">
                        <Label className="text-base font-semibold">Feature Controls</Label>
                        
                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <Label className="text-sm font-medium">Enable Sourcing Labs</Label>
                                <p className="text-xs text-muted-foreground">Allow users to access sourcing training</p>
                            </div>
                            <Switch
                                checked={sourcingSettings.enabled}
                                onCheckedChange={(checked) => updateSourcingSetting('enabled', checked)}
                                data-testid="sourcing-enabled"
                            />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <Label className="text-sm font-medium">Dynamic Difficulty</Label>
                                <p className="text-xs text-muted-foreground">Auto-adjust based on performance</p>
                            </div>
                            <Switch
                                checked={sourcingSettings.dynamic_difficulty_enabled}
                                onCheckedChange={(checked) => updateSourcingSetting('dynamic_difficulty_enabled', checked)}
                                data-testid="sourcing-dynamic-diff"
                            />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <Label className="text-sm font-medium">AI Hints</Label>
                                <p className="text-xs text-muted-foreground">Allow AI assistance during sessions</p>
                            </div>
                            <Switch
                                checked={sourcingSettings.ai_hints_enabled}
                                onCheckedChange={(checked) => updateSourcingSetting('ai_hints_enabled', checked)}
                                data-testid="sourcing-hints"
                            />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <Label className="text-sm font-medium">Show Answers After</Label>
                                <p className="text-xs text-muted-foreground">Reveal correct answers on completion</p>
                            </div>
                            <Switch
                                checked={sourcingSettings.show_correct_answers_after}
                                onCheckedChange={(checked) => updateSourcingSetting('show_correct_answers_after', checked)}
                                data-testid="sourcing-show-answers"
                            />
                        </div>

                        <div className="flex items-center justify-between p-3 rounded-lg border">
                            <div>
                                <Label className="text-sm font-medium">Allow Retry</Label>
                                <p className="text-xs text-muted-foreground">Allow users to retry completed labs</p>
                            </div>
                            <Switch
                                checked={sourcingSettings.allow_retry}
                                onCheckedChange={(checked) => updateSourcingSetting('allow_retry', checked)}
                                data-testid="sourcing-allow-retry"
                            />
                        </div>
                    </div>

                    {/* Numeric Settings */}
                    <div className="space-y-4">
                        <Label className="text-base font-semibold">Thresholds & Weights</Label>

                        <div className="p-3 rounded-lg border space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Candidates Per Session</Label>
                                <Badge variant="outline">{sourcingSettings.candidates_per_session}</Badge>
                            </div>
                            <Slider
                                value={[sourcingSettings.candidates_per_session]}
                                onValueChange={([value]) => updateSourcingSetting('candidates_per_session', value)}
                                min={5}
                                max={20}
                                step={1}
                            />
                            <p className="text-xs text-muted-foreground">Number of candidates generated per session</p>
                        </div>

                        <div className="p-3 rounded-lg border space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Min Evaluations Required</Label>
                                <Badge variant="outline">{sourcingSettings.min_evaluations_required}</Badge>
                            </div>
                            <Slider
                                value={[sourcingSettings.min_evaluations_required]}
                                onValueChange={([value]) => updateSourcingSetting('min_evaluations_required', value)}
                                min={3}
                                max={15}
                                step={1}
                            />
                            <p className="text-xs text-muted-foreground">Minimum candidates to evaluate before proceeding</p>
                        </div>

                        <div className="p-3 rounded-lg border space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Score Threshold for Increase</Label>
                                <Badge variant="outline">{sourcingSettings.score_threshold_for_increase}%</Badge>
                            </div>
                            <Slider
                                value={[sourcingSettings.score_threshold_for_increase]}
                                onValueChange={([value]) => updateSourcingSetting('score_threshold_for_increase', value)}
                                min={50}
                                max={95}
                                step={5}
                            />
                            <p className="text-xs text-muted-foreground">Score needed to progress difficulty</p>
                        </div>

                        <div className="p-3 rounded-lg border space-y-3">
                            <Label className="text-sm font-medium">Scoring Weights</Label>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-center p-2 rounded bg-muted/50">
                                    <p className="font-medium">{Math.round((sourcingSettings.search_string_weight ?? 0.2) * 100)}%</p>
                                    <p className="text-muted-foreground">Search</p>
                                </div>
                                <div className="text-center p-2 rounded bg-muted/50">
                                    <p className="font-medium">{Math.round((sourcingSettings.candidate_selection_weight ?? 0.4) * 100)}%</p>
                                    <p className="text-muted-foreground">Selection</p>
                                </div>
                                <div className="text-center p-2 rounded bg-muted/50">
                                    <p className="font-medium">{Math.round((sourcingSettings.reasoning_quality_weight ?? 0.4) * 100)}%</p>
                                    <p className="text-muted-foreground">Reasoning</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default Settings;
